import { useSearchStore } from "@app/stores/useSearchStore"
import { useHead, useRequestURL, useRouter, useSeoMeta } from "nuxt/app";
import type {
	UseHeadInput,
	UseSeoMetaInput
} from "@unhead/vue/types";
import { watch } from "vue";
import IExtendedSearchQueryFilter from "@app/interfaces/dtos/IExtendedSearchQueryFilter";
import { UseMetaVariablesWatcherOptions } from '@app/interfaces/useMetaVariablesWatcher.interface';
import { FilterProcessingService } from "@app/routing/FilterProcessingService";
import { usePortalMap } from "./usePortalMap";
import { getPortalTypeFromEnvClient } from "@app/utils/get-portal-type-from-env.client";
import { getPortalTypeFromEnvServer } from "@app/utils/get-portal-type-from-env.server";

export const useGetDomain = (): string | undefined => {
	const portalMap = usePortalMap();
	const portalType = import.meta.server ? getPortalTypeFromEnvServer() : getPortalTypeFromEnvClient();
	const origin = portalMap.get(portalType);

	return origin || useRequestURL().origin;
}

const useCanonicalPath = (filterSelection: IExtendedSearchQueryFilter[]):string =>{
	const router = useRouter();
	const currentRoute = router.currentRoute.value;
	const origin = useGetDomain();
	const searchStore = useSearchStore();
	const hasEnoughResults = searchStore.pageData?.seoMeta?.hasEnoughResults;

	if(filterSelection.length === 0) {
		return `${origin}${router.resolve({ path: currentRoute.path }).path}`;
	}

	try {
		const indexableFilters = hasEnoughResults
			? filterSelection.filter(f => f.isIndexable)
			: [];

		const route = FilterProcessingService.getInstance().processFilters(router, indexableFilters);
		const path = router.resolve(route).path;

		return `${origin}${path}`;
	} catch {
		return `${origin}${router.resolve({ path: currentRoute.path }).path}`;
	}
}

const useSetHeadSeoMeta = (
	options: UseMetaVariablesWatcherOptions
):void => {
	const canonicalPath = useCanonicalPath(options.filterSelection);

	if (import.meta.server) {
		const image = "https://monet-prtl-co.imgix.net/Shared/SearchPage/BA_Portal_no%20text_illustration.png?auto=compress&h=400&w=auto";
		useSeoMeta({
			charset: "utf-8",
			viewport: "width=device-width, initial-scale=1",
			ogType: "website",
			ogImage: image,
		});
	}

	useSeoMeta({
		title: () => options.metaInfoExtended.metaTitle,
		description: () => options.metaInfoExtended.metaDescription,
		robots: () => options.metaInfoExtended.robots,
		ogTitle: () => options.metaInfoExtended.metaTitle,
		ogDescription: () => options.metaInfoExtended.metaDescription,
		...(options.seoMetaInput || {}),
	});

	useHead(() => ({
		link: [
			{ rel: "canonical", href: canonicalPath },
		],
		...(options.headInput || {}),
	}));
};

export const useMetaVariablesWatcher = (headInput?: UseHeadInput, seoMetaInput?: UseSeoMetaInput):void => {
	const searchStore = useSearchStore();

	const pageTitle = searchStore.pageData?.seoMeta?.pageTitle || "";
	const metaTitle = searchStore.pageData?.seoMeta?.metaTitle || "";
	const metaDescription = searchStore.pageData?.seoMeta?.metaDescription || "";
	const robots = searchStore.pageData?.seoMeta?.robots || "noindex, noarchive";
	const pageTitleWithoutResultsCount = searchStore.pageData?.seoMeta?.pageTitleWithoutResultsCount || "";

	useHead(() => {
		if (!searchStore.pageData) return {};

		const { schema } = searchStore.pageData;

		return {
			script: [{
				type: "application/ld+json",
				innerHTML: JSON.stringify(schema),
			}]
		};
	});

	useSetHeadSeoMeta({
		filterSelection: searchStore.filtersSelection,
		metaInfoExtended: { pageTitle, metaTitle, metaDescription, robots, pageTitleWithoutResultsCount },
		headInput,
		seoMetaInput,
	});

	searchStore.setPageTitle(pageTitle);

	watch(() => searchStore.pageData?.seoMeta, (newSeoMeta) => {
		if(!newSeoMeta) return;

		searchStore.setPageTitle(newSeoMeta.pageTitle);

		useSetHeadSeoMeta({
			filterSelection: searchStore.filtersSelection,
			metaInfoExtended: {
				pageTitle: newSeoMeta.pageTitle,
				metaTitle: newSeoMeta.metaTitle,
				metaDescription: newSeoMeta.metaDescription,
				robots: newSeoMeta.robots,
				pageTitleWithoutResultsCount: newSeoMeta.pageTitleWithoutResultsCount
			},
			headInput,
			seoMetaInput,
		});
	}, { immediate: true });
}
